import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Pencil as EditIcon, Trash2 as DeleteIcon, Plus as AddIcon, Play as PlayIcon } from 'lucide-react';
import { Button, Chip, ShellHeader, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@coordio/ui';
import { FormSection, InlineErrorPanel, LoadingSkeleton, PageContainer } from '@coordio/ui/layout';
import { externalSystemService, type ExternalSystemDetailDto, type ExternalApiMethodDto } from '../services/externalSystemService';
import { useNotificationStore } from '../store/notificationStore';
import { ApiMethodForm } from '../components/externalSystems/ApiMethodForm';
import { ApiMethodTester } from '../components/externalSystems/ApiMethodTester';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import EmptyState from '../components/data-display/EmptyState';
import { useConfirmDialog } from '../components/common/ConfirmDialog';

/**
 * External system detail page component.
 */
export const ExternalSystemDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();
  const confirm = useConfirmDialog();

  const [system, setSystem] = useState<ExternalSystemDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingMethodId, setEditingMethodId] = useState<string | null>(null);
  const [testingMethodId, setTestingMethodId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('api-methods');

  useEffect(() => {
    if (id) {
      loadSystem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadSystem = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await externalSystemService.getExternalSystem(id);
      setSystem(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load external system');
      addNotification('Failed to load external system', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    const ok = await confirm({
      title: 'Delete external system',
      message: 'Are you sure you want to delete this external system? This will also delete all associated API methods.',
      confirmText: 'Delete',
    });
    if (!ok) return;

    try {
      await externalSystemService.deleteExternalSystem(id);
      addNotification('External system deleted successfully', 'success');
      navigate('/external-systems');
    } catch (err: any) {
      addNotification(err.message || 'Failed to delete external system', 'error');
    }
  };

  const handleDeleteMethod = async (methodId: string) => {
    const ok = await confirm({
      title: 'Delete API method',
      message: 'Are you sure you want to delete this API method?',
      confirmText: 'Delete',
    });
    if (!ok) return;

    addNotification(`Delete API method (${methodId}) functionality coming soon`, 'info');
  };

  const apiMethodColumns: DataTableColumn<ExternalApiMethodDto>[] = [
    {
      id: 'name',
      label: 'Name',
      minWidth: 200,
      render: (method) => (
        <div>
          <p className="text-sm font-medium">{method.name}</p>
          {method.description && (
            <span className="text-xs text-[var(--color-text-secondary)]">{method.description}</span>
          )}
        </div>
      ),
    },
    {
      id: 'httpMethod',
      label: 'HTTP Method',
      minWidth: 120,
      render: (method) => <Chip className="text-xs" variant="outlined" label={method.httpMethod} />,
    },
    {
      id: 'endpointPath',
      label: 'Endpoint',
      minWidth: 200,
      render: (method) => (
        <p className="text-sm font-mono">{method.endpointPath}</p>
      ),
    },
    {
      id: 'toolName',
      label: 'Tool Name',
      minWidth: 150,
    },
    {
      id: 'riskLevel',
      label: 'Risk Level',
      minWidth: 120,
      render: (method) => (
        <Chip
          className="text-xs"
          color={
            method.riskLevel === 'Critical'
              ? 'red'
              : method.riskLevel === 'High'
              ? 'amber'
              : method.riskLevel === 'Medium'
              ? 'blue'
              : 'default'
          }
          label={method.riskLevel}
        />
      ),
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 100,
      render: (method) => (
        <Chip className="text-xs" color={method.isActive ? 'green' : 'default'} label={method.isActive ? 'Active' : 'Inactive'} />
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 150,
      align: 'right',
      render: (method) => (
        <div className="flex gap-1 justify-end">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  data-testid={`external-system-test-method-${method.id}`}
                  onClick={() => setTestingMethodId(method.id)}
                >
                  <PlayIcon size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Test</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  data-testid={`external-system-edit-method-${method.id}`}
                  onClick={() => setEditingMethodId(method.id)}
                >
                  <EditIcon size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Edit</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="text-[var(--color-text-danger)]"
                  data-testid={`external-system-delete-method-${method.id}`}
                  onClick={() => handleDeleteMethod(method.id)}
                >
                  <DeleteIcon size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Delete</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <>
        <ShellHeader title="External System" description="Loading…" data-testid="external-system-header" />
        <PageContainer data-testid="external-system-form-page">
          <LoadingSkeleton rows={6} data-testid="external-system-loading" />
        </PageContainer>
      </>
    );
  }

  if (error && !system) {
    return (
      <>
        <ShellHeader
          title="External System"
          description="External system not found."
          data-testid="external-system-header"
          actions={
            <Button type="button" variant="outline" size="sm" onClick={() => navigate('/external-systems')} data-testid="back-external-system-btn">
              Back to External Systems
            </Button>
          }
        />
        <PageContainer data-testid="external-system-form-page">
          <InlineErrorPanel
            message={error!}
            data-testid="external-system-error-panel"
            action={<Button type="button" variant="ghost" size="sm" onClick={() => setError(null)}>Dismiss</Button>}
          />
        </PageContainer>
      </>
    );
  }

  const tabs = system ? [
    { label: 'API Methods', value: 'api-methods' },
    { label: editingMethodId && editingMethodId !== 'new' ? 'Edit Method' : 'Add Method', value: 'edit-method' },
    ...(testingMethodId ? [{ label: 'Test Method', value: 'test-method' }] : []),
  ] : [];

  return (
    <>
      <ShellHeader
        title={system?.name || 'External System'}
        description={system?.description}
        data-testid="external-system-header"
        actions={
          system ? (
            <div className="flex items-center gap-2">
              <Chip className="text-xs" variant="outlined" label={`Base URL: ${system.baseUrl}`} />
              <Chip className="text-xs" variant="outlined" label={`Auth: ${system.authenticationType}`} />
              <Chip className="text-xs" label={`${system.apiMethodCount} API Methods`} />
              <Chip className="text-xs" color={system.isActive ? 'green' : 'default'} label={system.isActive ? 'Active' : 'Inactive'} />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2"
                data-testid="edit-external-system-btn"
                onClick={() => navigate(`/external-systems/${id}/edit`)}
              >
                <EditIcon size={16} />
                Edit
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                className="gap-2"
                data-testid="delete-external-system-btn"
                onClick={handleDelete}
              >
                <DeleteIcon size={16} />
                Delete
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => navigate('/external-systems')} data-testid="back-external-system-btn">
                Back
              </Button>
            </div>
          ) : undefined
        }
      />
      <PageContainer data-testid="external-system-form-page">
        {error && (
          <InlineErrorPanel
            message={error}
            data-testid="external-system-error-panel"
            action={<Button type="button" variant="ghost" size="sm" onClick={() => setError(null)}>Dismiss</Button>}
          />
        )}

        {system && (
          <>
            <div className="page-detail-layout__tabs" role="tablist" aria-label="External system sections">
              {tabs.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.value}
                  className="page-detail-layout__tab"
                  data-testid={`external-system-tab-${tab.value}`}
                  onClick={() => setActiveTab(tab.value)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === 'api-methods' && (
              <div role="tabpanel" className="page-detail-layout__panel" data-testid="external-system-panel-api-methods">
                <FormSection title="API Methods" description="Manage the external API methods for this system." data-testid="external-system-section-api-methods">
                  <div className="mb-3 flex justify-end">
                    <Button
                      type="button"
                      variant="default"
                      size="sm"
                      className="gap-2"
                      data-testid="external-system-add-api-method-btn"
                      onClick={() => setEditingMethodId('new')}
                    >
                      <AddIcon size={16} />
                      Add API Method
                    </Button>
                  </div>
                  <DataTable
                    columns={apiMethodColumns}
                    rows={system.apiMethods}
                    loading={false}
                    emptyState={
                      <EmptyState
                        title="No API methods defined"
                        description="Add an API method to get started."
                        actionLabel="Add API Method"
                        onAction={() => setEditingMethodId('new')}
                      />
                    }
                  />
                </FormSection>
              </div>
            )}

            {activeTab === 'edit-method' && (
              <div role="tabpanel" className="page-detail-layout__panel" data-testid="external-system-panel-edit-method">
                <FormSection title={editingMethodId && editingMethodId !== 'new' ? 'Edit Method' : 'Add Method'} data-testid="external-system-section-edit-method">
                  <ApiMethodForm
                    systemId={id!}
                    methodId={editingMethodId === 'new' ? undefined : editingMethodId || undefined}
                    onSuccess={() => {
                      setEditingMethodId(null);
                      setActiveTab('api-methods');
                      loadSystem();
                    }}
                  />
                </FormSection>
              </div>
            )}

            {activeTab === 'test-method' && testingMethodId && (
              <div role="tabpanel" className="page-detail-layout__panel" data-testid="external-system-panel-test-method">
                <FormSection title="Test Method" data-testid="external-system-section-test-method">
                  <ApiMethodTester systemId={id!} methodId={testingMethodId} />
                  <div className="ui-form-actions">
                    <Button variant="outline" data-testid="back-external-system-methods-btn" onClick={() => { setTestingMethodId(null); setActiveTab('api-methods'); }}>
                      Back to Methods
                    </Button>
                  </div>
                </FormSection>
              </div>
            )}
          </>
        )}
      </PageContainer>
    </>
  );
};
